import {FormEvent, useMemo, useState} from "react";
import {Alert, Form} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {ProfileApi} from "../../api/ProfileApi";
import {SupportApi} from "../../api/SupportApi";
import {SUPPORT_MESSAGE_MAX_LENGTH, SUPPORT_NAME_MAX_LENGTH, SUPPORT_SUBJECT_MAX_LENGTH} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {Field, FieldControl, FieldSelect} from "../../components/Field";
import {SubmitButton} from "../../components/SubmitButton";
import {announce} from "../../components/announce";
import {emailProblem} from "../../components/format";
import {turnstileAvailable} from "../../components/Turnstile";
import {useTurnstile} from "../../components/useTurnstile";
import {AttachmentPicker, UploadProgress, useUploadProgress} from "./SupportParts";
import {format, rich, support} from "../../i18n";

const messages = support.newTicketPage;
// The license choice for a ticket that isn't about one.
const GENERAL = 'general';

// Opening a ticket, signed in or not. Signed in: about one of the user's licenses that hasn't been revoked, or a general
// question. Signed out: name and email instead (no files: they can add them later), and the API emails a private link
// to the ticket. Every new ticket passes Cloudflare Turnstile's bot check first.
export function NewSupportTicketPage() {
    const navigate = useNavigate();
    const {user, loading: authLoading} = useAuth();
    const {data: licenses, error: loadError, loading} = useAsync(() => user ? ProfileApi.licenses() : Promise.resolve([]), [user?.username]);
    const active = useMemo(() => (licenses ?? []).filter(x => !x.revoked), [licenses]);
    const [licenseId, setLicenseId] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const turnstile = useTurnstile();
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [sentTo, setSentTo] = useState<string>();
    const upload = useUploadProgress();

    const chosenLicense = licenseId || (active.length === 1 ? active[0].id : '');
    const problems = {
        name: !user && !name.trim() ? messages.problems.name : undefined,
        email: !user ? (!email.trim() ? messages.problems.email : emailProblem(email)) : undefined,
        license: user && active.length > 0 && !chosenLicense ? messages.problems.license : undefined,
        subject: !subject.trim() ? messages.problems.subject : undefined,
        body: !body.trim() ? messages.problems.body : undefined
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (Object.values(problems).some(Boolean)) {
            return;
        }
        setBusy(true);
        setError(undefined);
        const turnstileToken = await turnstile.waitForToken();
        if (!turnstileToken) {
            setBusy(false);
            return;
        }
        upload.start(files);
        try {
            const opened = await SupportApi.open({
                ...(user
                    ? (chosenLicense && chosenLicense !== GENERAL ? { licenseId: chosenLicense } : {})
                    : { name: name.trim(), email: email.trim() }),
                subject: subject.trim(),
                body: body.trim(),
                turnstileToken
            }, user ? files : [], upload.onProgress);
            announce(messages.opened);
            if (user) {
                navigate(`/support/${opened.id}`);
            } else {
                setSentTo(email.trim());
            }
        } catch (err) {
            setError(err);
            turnstile.reset();
        } finally {
            setBusy(false);
            upload.done();
        }
    };

    const header = (
        <>
            {user && <div className="mb-2"><Link to="/support" className="small"><i className="bi bi-arrow-left me-1" aria-hidden="true"></i>{messages.back}</Link></div>}
            <PageHeader title={messages.title}/>
        </>
    );
    if (authLoading || (user && loading && !licenses)) {
        return <>{header}<LoadingSpinner/></>;
    }
    if (!turnstileAvailable()) {
        return <>{header}<Alert variant="secondary">{messages.unavailable}</Alert></>;
    }
    if (sentTo) {
        return <>{header}<Alert variant="success" role="status">{format(messages.checkEmail, { email: sentTo })}</Alert></>;
    }

    return (
        <>
            {header}
            <ErrorAlert error={loadError}/>
            {!user && (
                <p className="text-body-secondary">
                    {rich(messages.signedOutIntro, { link: text => <Link to={`/login?returnTo=${encodeURIComponent('/support/new')}`}>{text}</Link> })}
                </p>
            )}
            <Form noValidate onSubmit={submit} className="support-form">
                <ErrorAlert error={error} shownFields={['name', 'email', 'subject', 'body']} messages={{
                    429: user ? messages.tooMany : messages.tooManySignedOut,
                    turnstile_failed: messages.turnstileFailed,
                    503: messages.unavailable
                }}/>
                {!user && (
                    <>
                        <Field className="mb-3" controlId="ticket-name" label={messages.name} error={(validated && problems.name) || fieldError(error, 'name')}>
                            <FieldControl value={name} onChange={e => setName(e.target.value)} maxLength={SUPPORT_NAME_MAX_LENGTH} autoComplete="name" disabled={busy}/>
                        </Field>
                        <Field className="mb-3" controlId="ticket-email" label={messages.email} help={messages.emailHelp}
                               error={(validated && problems.email) || fieldError(error, 'email')}>
                            <FieldControl type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={busy}/>
                        </Field>
                    </>
                )}
                {user && active.length > 0 && (
                    <Field className="mb-3" controlId="ticket-license" label={messages.license} error={validated && problems.license}>
                        <FieldSelect value={chosenLicense} onChange={e => setLicenseId(e.target.value)} disabled={busy}>
                            {!chosenLicense && <option value="">{messages.chooseLicense}</option>}
                            {active.map(x => <option key={x.id} value={x.id}>{format(messages.licenseOption, { product: x.productName || support.unknownProduct, edition: x.edition, key: x.key })}</option>)}
                            <option value={GENERAL}>{messages.generalOption}</option>
                        </FieldSelect>
                    </Field>
                )}
                <Field className="mb-3" controlId="ticket-subject" label={messages.subject} error={(validated && problems.subject) || fieldError(error, 'subject')}>
                    <FieldControl value={subject} onChange={e => setSubject(e.target.value)} maxLength={SUPPORT_SUBJECT_MAX_LENGTH} disabled={busy}/>
                </Field>
                <Field className="mb-3" controlId="ticket-body" label={messages.body} error={(validated && problems.body) || fieldError(error, 'body')}
                       help={<>{messages.bodyHelp} {messages.englishOnly}</>}>
                    <FieldControl as="textarea" rows={8} value={body} onChange={e => setBody(e.target.value)} maxLength={SUPPORT_MESSAGE_MAX_LENGTH} disabled={busy}/>
                </Field>
                {user
                    ? <div className="mb-4"><AttachmentPicker files={files} onChange={setFiles} disabled={busy}/></div>
                    : <p className="small text-body-secondary mb-4">{messages.filesLater}</p>}
                <div className="mb-3">
                    {turnstile.widget}
                    {turnstile.failed && <Alert variant="warning">{messages.turnstileUnavailable}</Alert>}
                </div>
                <SubmitButton busy={busy}>{messages.submit}</SubmitButton>
                <UploadProgress fraction={upload.fraction}/>
            </Form>
        </>
    );
}
